/**
 * Automotive panel widgets — navigation.
 */
import { registerAutomotive } from '../../registryCore';
import { bool, num, setState, str } from '../../helpers';
import { addCompassRose, addOsmMapLayer, addPanelFrame, addPanelTitle, addTurnArrow, finishPanel, fitLabel, panelBounds, panelGroup, panelTheme, textAt, } from '../panelPrimitives';
import { fluidFont } from '../../layout';
import { lines } from './shared';
registerAutomotive('compass', function (props, app) {
    var theme = panelTheme(props);
    var bounds = panelBounds(props, 140, 140);
    var group = panelGroup(app, 'compass', props, bounds);
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    addPanelFrame(group, app, bounds, theme);
    var heading = num(props, 'heading', num(props, 'value', 45));
    var radius = Math.max(22, Math.min(w, h) * 0.32);
    var cx = pad + w / 2;
    var cy = pad + h / 2 - 4;
    addCompassRose(group, app, cx, cy, radius, heading, theme);
    var label = "".concat(['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(heading / 45) % 8], " ").concat(String(Math.round(heading)).padStart(3, '0'), "\u00B0");
    var labelSize = fluidFont(11, bounds, 8, 13);
    group.add(app.text({
        text: label,
        x: pad + w / 2,
        y: textAt(pad + h - 10, labelSize),
        fontSize: labelSize,
        fontWeight: 'bold',
        fill: theme.text,
        textAlign: 'center',
        listening: false,
    }));
    group.metadata.refresh = function (v) { return setState(group, { heading: v, value: v }); };
    return finishPanel(group, props, bounds, { heading: heading, value: heading });
});
registerAutomotive('gpsNavigationMap', function (props, app) {
    var theme = panelTheme(props);
    var bounds = panelBounds(props, 240, 160);
    var group = panelGroup(app, 'gpsNavigationMap', props, bounds);
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    addPanelFrame(group, app, bounds, theme);
    var contentY = addPanelTitle(group, app, bounds, theme, 'Navigation');
    addOsmMapLayer(group, app, pad, contentY, w, h - (contentY - pad) - 4, theme, {
        lat: num(props, 'lat', 51.505),
        lon: num(props, 'lon', -0.09),
        zoom: num(props, 'zoom', 14),
        route: true,
        marker: true,
        useTile: bool(props, 'useOsmTiles', true),
    });
    return finishPanel(group, props, bounds);
});
registerAutomotive('navigationSearch', function (props, app) {
    var _a;
    var theme = panelTheme(props);
    var bounds = panelBounds(props, 220, 130);
    var group = panelGroup(app, 'navigationSearch', props, bounds);
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    addPanelFrame(group, app, bounds, theme, true);
    var query = str(props, 'query', (_a = lines(props, ['Search…'])[0]) !== null && _a !== void 0 ? _a : 'Search…');
    var fieldH = Math.max(22, h * 0.15);
    var fieldY = pad + 10;
    group.add(app.roundedRect({
        x: pad,
        y: fieldY,
        width: w,
        height: fieldH,
        cornerRadius: fieldH / 2,
        fill: '#1f2937',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.text({
        text: '⌕',
        x: pad + 10,
        y: textAt(fieldY + fieldH / 2, 12),
        fontSize: 12,
        fill: theme.textMuted,
        listening: false,
    }));
    group.add(fitLabel(app, query, pad + 28, fieldY + fieldH * 0.28, w - 36, fluidFont(10, bounds, 8, 11), theme));
    var mapY = fieldY + fieldH + 8;
    addOsmMapLayer(group, app, pad, mapY, w, pad + h - mapY - 4, theme, {
        lat: num(props, 'lat', 51.51),
        lon: num(props, 'lon', -0.12),
        zoom: 13,
        route: false,
        marker: false,
        useTile: bool(props, 'useOsmTiles', true),
    });
    return finishPanel(group, props, bounds, { query: query });
});
registerAutomotive('routeGuidance', function (props, app) {
    var _a, _b;
    var theme = panelTheme(props);
    var bounds = panelBounds(props, 220, 130);
    var group = panelGroup(app, 'routeGuidance', props, bounds);
    var pad = bounds.pad, w = bounds.innerWidth, h = bounds.innerHeight;
    var rowLines = lines(props, ['12.4 km', '18 min']);
    var distance = str(props, 'distance', (_a = rowLines[0]) !== null && _a !== void 0 ? _a : '12.4 km');
    var eta = str(props, 'eta', (_b = rowLines[1]) !== null && _b !== void 0 ? _b : '18 min');
    var instruction = str(props, 'instruction', 'Turn right onto Main St');
    addPanelFrame(group, app, bounds, theme);
    var mapH = Math.max(48, h * 0.4);
    addOsmMapLayer(group, app, pad, pad + 4, w, mapH, theme, {
        lat: num(props, 'lat', 51.505),
        lon: num(props, 'lon', -0.09),
        zoom: 15,
        route: true,
        marker: true,
        useTile: bool(props, 'useOsmTiles', true),
    });
    var infoY = pad + mapH + 12;
    var infoH = pad + h - infoY;
    var arrowSize = Math.min(infoH, w * 0.22);
    addTurnArrow(group, app, pad, infoY, arrowSize, theme);
    var textX = pad + arrowSize + 8;
    var textW = w - arrowSize - 12;
    group.add(fitLabel(app, instruction, textX, infoY + 4, textW, fluidFont(10, bounds, 8, 11), theme, true), app.text({
        text: "".concat(distance, " \u00B7 ").concat(eta),
        x: textX,
        y: textAt(infoY + infoH - 12, 9),
        fontSize: fluidFont(9, bounds, 7, 10),
        fill: theme.textMuted,
        listening: false,
    }));
    return finishPanel(group, props, bounds, { distance: distance, eta: eta, instruction: instruction });
});
